import { FormError } from 'app/shared/model/Form';
import * as React from "react";
import { Form, Input, Switch, Select, Row, Col, DatePicker } from 'antd';
import stringHelper from "../../../helpers/stringHelper";
import './FormField.css';
import DateHelper, { CompareTypes } from 'app/helpers/dateHelper';
import DateField from 'app/shared/ui/DateField';
import { FormErrorsContext } from './FormErrorsContext';

const { TextArea } = Input;

const ANY_UNITS_SENTINEL = 2147483647;

const isAnySentinel = (value: any): boolean =>
    Number(value) === ANY_UNITS_SENTINEL;

interface FormFieldsProps {
    name: string,
    type: string,
    required?: boolean,
    defaultValue?: any,

    maxLength?: number,
    pattern?: string,
    allowAny?: boolean,
    step?: string,
    decimalPlace?: number,
    greaterThan?: string,
    max?: string,

    commitOnBlur?: boolean,
    selectAllOnFirstFocus?: boolean,

    standalone?: boolean,
    immediateEdit?: boolean,

    dropdownOptions?: { key: string, value: string, text: string }[],

    editMode: boolean,
    formErrors?: FormError[],
    onError: (formErrors: FormError[]) => void,
    onChange?: (value: string) => void
}

const EMPTY_ERRORS: FormError[] = [];

const FormField = (props: FormFieldsProps) => {
    const contextErrors = React.useContext(FormErrorsContext);
    const formErrors = contextErrors ?? props.formErrors ?? EMPTY_ERRORS;

    const formatValue = (value: any, allowIncomplete: boolean = false): string => {
        if (isAnySentinel(value))
        {
            return 'Any';
        }
        if(props.type === 'number' && props.decimalPlace)
        {
            if (allowIncomplete && typeof value === 'string') {
                const partialNumberPattern = /^-?(\d+\.?\d*|\.\d*)$/;
                if (value === '' || partialNumberPattern.test(value)) {
                    return value;
                }
            }
            const numValue = Number(value);
            if (isNaN(numValue)) {
                return value;
            }
            return numValue.toFixed(props.decimalPlace);
        }
        else if (props.type === 'date' && value && value !== '')
        {
            return DateHelper.ToDateString(value);
        }
        else
        {
            return value;
        }
    }

    const [error, setError] = React.useState<string>(
        formErrors.find((e) => e.name === props.name)?.error ?? ''
    );

    const [isAny, setIsAny] = React.useState<boolean>(
        !!(props.allowAny && isAnySentinel(props.defaultValue))
    );

    const [value, setValue] = React.useState<string>(formatValue(props.defaultValue));
    const [editorActive, setEditorActive] = React.useState(false);

    const isDeferredNumberInput =
        props.type === 'number' &&
        props.commitOnBlur === true;

    const shouldSelectAllOnFocus = props.selectAllOnFirstFocus === true;
    const useLazyEditor =
        props.standalone === true &&
        props.immediateEdit !== true &&
        props.type !== 'dropdown' &&
        props.type !== 'date' &&
        !props.allowAny;

    React.useEffect(() => {
        if (!props.editMode) {
            setEditorActive(false);
        }
    }, [props.editMode]);

    const inputWrapRef = React.useRef<HTMLDivElement | null>(null);
    React.useEffect(() => {
        if (!editorActive || !useLazyEditor) return;
        const input = inputWrapRef.current?.querySelector('input');
        if (input) {
            input.focus();
            if (shouldSelectAllOnFocus) {
                input.select();
            }
        }
    }, [editorActive, useLazyEditor, shouldSelectAllOnFocus]);

    const isFocusedRef = React.useRef(false);
    const suppressNextMouseUpRef = React.useRef(false);
    const formErrorsRef = React.useRef(formErrors);
    formErrorsRef.current = formErrors;
    const onErrorRef = React.useRef(props.onError);
    onErrorRef.current = props.onError;
    const onChangeRef = React.useRef(props.onChange);
    onChangeRef.current = props.onChange;

    React.useEffect(() => {
        if (isFocusedRef.current) {
            return;
        }

        setValue(formatValue(props.defaultValue));
        setIsAny(!!(props.allowAny && isAnySentinel(props.defaultValue)));
    }, [props.defaultValue, props.allowAny]);

    React.useEffect(() => {
        const external = formErrors.find((e) => e.name === props.name);
        const next = external?.error ?? '';
        setError((prev) => (prev === next ? prev : next));
    }, [formErrors, props.name]);

    const handleOnChange = (val: string, notifyParent: boolean = true) => {
        let nextError = '';
        let fieldName = stringHelper.toTitleCase(props.name);
        const currentErrors = formErrorsRef.current;
        let errorIndex = currentErrors.findIndex(e => e.name === props.name);
        let errorList = [...currentErrors];
        const treatingAsAny = isAny || isAnySentinel(val);

        // Required
        if(props.required && !treatingAsAny && (!val || val.length === 0))
        {
            nextError = `${fieldName} is required!`;
        }

        // Max Length
        if(nextError === '' && props.maxLength && val.length > props.maxLength)
        {
            nextError = `Value for ${fieldName} should be less than ${props.maxLength}!`;
        }
        
        // Greater Than (number / date)
        if (
            nextError === '' &&
            !treatingAsAny &&
            (
                (props.type === 'number' && props.greaterThan && Number(val) < Number(props.greaterThan)) ||
                (props.type === 'date' && props.greaterThan && DateHelper.CompareDates(val, props.greaterThan, CompareTypes.SmallerThan))
            )
        ) {
            nextError = `Value must be greater or equal to ${formatValue(props.greaterThan)}`;
        }

        //Max
        if(
            nextError === '' &&
            !treatingAsAny &&
            (
                (props.type === 'number' && props.max && Number(val) > Number(props.max)) ||
                (props.type === 'date' && props.max && DateHelper.CompareDates(val, props.max, CompareTypes.BiggerThan))
            )
        ) {
            nextError = `Value must be less than or equal to ${props.max}`;
        }

        if (nextError !== ''){
            if(errorIndex >= 0){
                errorList[errorIndex] = { ...errorList[errorIndex], error: nextError };
            } else {
                errorList.push({
                error: nextError,
                name: props.name,
                message: '',
                field: undefined
                });
        }
        } else if (errorIndex >= 0) {
            errorList.splice(errorIndex, 1);
        }

        const prevError = errorIndex >= 0 ? currentErrors[errorIndex]?.error ?? '' : '';
        const errorChanged = prevError !== nextError;
        setError(nextError);

        if (notifyParent && onChangeRef.current) {
            onChangeRef.current(val);
        }

        const shouldSyncErrors = errorChanged && (notifyParent || !isDeferredNumberInput);
        if (shouldSyncErrors) {
            onErrorRef.current(errorList);
        }
    };

    const handleInputFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = true;

        if (!shouldSelectAllOnFocus) return;

        suppressNextMouseUpRef.current = true;
        const el = e.target;

        requestAnimationFrame(() => {
            if (document.activeElement === el) {
                el.select();
            }
        });
    };

    const handleInputMouseUp = (e: React.MouseEvent<HTMLInputElement>) => {
        if (!suppressNextMouseUpRef.current) return;

        e.preventDefault();
        suppressNextMouseUpRef.current = false;
    };

    const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        isFocusedRef.current = false;
        suppressNextMouseUpRef.current = false;

        if (props.type === 'number' && props.decimalPlace) {
            const formatted = formatValue(e.target.value, false);
            setValue(formatted);

            if (isDeferredNumberInput || formatted !== e.target.value) {
                handleOnChange(formatted, true);
            }
            if (useLazyEditor) {
                setEditorActive(false);
            }
            return;
        }

        if (props.type === 'number' && isDeferredNumberInput) {
            handleOnChange(e.target.value, true);
        }
        if (useLazyEditor) {
            setEditorActive(false);
        }
    };

    const renderControl = () => {
        if (props.type === 'textarea') {
            return (
                <TextArea
                    size="small"
                    value={value}
                    status={error ? "error" : ""}
                    onChange={(e: { target: { value: any; }; }) => {
                        handleOnChange(String(e.target.value));
                        setValue(formatValue(e.target.value));
                    }}
                    {...props.maxLength && {maxLength: props.maxLength}}
                    {...props.required && {required: props.required}}
                    {...props.pattern && {pattern: props.pattern}}
                />
            );
        }
        if (props.type === "dropdown") {
            return (
                <Select
                    showSearch
                    placeholder="Strategy"
                    options={props.dropdownOptions?.map(opt => ({
                        label: opt.text,
                        value: opt.value,
                        key: opt.key
                    }))}
                    value={value}
                    onChange={(val: string) => {
                        handleOnChange(val);
                        setValue(formatValue(val));
                    }}
                    status={error ? "error" : ""}
                    style={{ width: "100%" }}
                    allowClear={!props.required}
                />
            );
        }
        if (props.type === 'date') {
            if (props.standalone) {
                return (
                    <DatePicker
                        picker='date'
                        defaultValue={value !== '' ? DateHelper.ToDayJS(value) : undefined}
                        format={DateHelper.DATE_PICKER_FORMATS}
                        onChange={(e) => { if (e) { handleOnChange(e.toISOString()); setValue(formatValue(e)); }}}
                        {...error && {status: error !== '' ? 'error' : ''}}
                        {...props.required && {required: props.required}}
                    />
                );
            }
            return (
                <DateField
                    value={value !== '' ? DateHelper.ToDateString(new Date(value), 'yyyy-MM-dd') : value}
                    hasError={!!error}
                    includeTodayButton={true}
                    onChange={(val) => {
                        handleOnChange(val);
                        setValue(formatValue(val));
                    }}
                    {...props.maxLength && {maxLength: props.maxLength}}
                    {...props.required && {required: props.required}}
                    {...props.pattern && {pattern: props.pattern}}
                />
            );
        }
        return (
            <Input
                size="small"
                type={props.type === 'number' && props.decimalPlace ? 'text' : props.type}
                inputMode={props.type === 'number' ? 'decimal' : undefined}
                name={props.name}
                value={value}
                status={error ? "error" : ""}
                onFocus={handleInputFocus}
                onMouseUp={handleInputMouseUp}
                onChange={(e: { target: { value: string } }) => {
                    const nextValue = e.target.value;

                    if (props.type === 'number' && props.decimalPlace) {
                        setValue(formatValue(nextValue, true));
                        handleOnChange(nextValue, !isDeferredNumberInput);
                    } else {
                        handleOnChange(nextValue, !isDeferredNumberInput);
                        setValue(formatValue(nextValue));
                    }
                }}
                onBlur={handleInputBlur}
                style={{ width: '100%' }}
                {...props.maxLength && {maxLength: props.maxLength}}
                {...props.required && {required: props.required}}
                {...props.pattern && {pattern: props.pattern}}
                {...props.step && {step: props.step}}
                {...props.greaterThan && {min: props.greaterThan}}
                {...props.max && {max: props.max}}
            />
        );
    };

    const fieldBody = props.allowAny ? (
        <Row gutter={8} align="middle" wrap={false}>
            <Col>
                <Switch checkedChildren="Any?"
                    checked={isAny}
                    onChange={(checked: boolean) => {
                        setIsAny(checked);
                        const nextValue = checked ? String(ANY_UNITS_SENTINEL) : '';
                        setValue(nextValue);
                        handleOnChange(nextValue, true);
                    }}
                    style={{ marginRight: 4 }}
                />
            </Col>
            {!isAny &&
                <Col flex="auto" style={{ minWidth: 64 }}>
                    {renderControl()}
                </Col>
            }
        </Row>
    ) : (
        renderControl()
    );

    if (props.standalone) {
        const showEditor =
            props.editMode && (props.immediateEdit === true || editorActive);

        if (!showEditor) {
            const display = formatValue(props.defaultValue);
            return (
                <div
                    ref={inputWrapRef}
                    className="form-field-standalone form-field-lazy"
                    role={props.editMode ? "button" : undefined}
                    tabIndex={props.editMode ? 0 : -1}
                    style={{
                        marginBottom: 0,
                        minHeight: 24,
                        padding: '1px 7px',
                        border: '1px solid transparent',
                        borderRadius: 4,
                        cursor: props.editMode ? 'text' : 'default',
                        width: '100%',
                    }}
                    onClick={props.editMode ? () => setEditorActive(true) : undefined}
                    onFocus={props.editMode ? () => setEditorActive(true) : undefined}
                    onKeyDown={props.editMode ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setEditorActive(true);
                        }
                    } : undefined}
                >
                    <span>{display === '' || display == null ? '\u00A0' : display}</span>
                    {error ? <div className="validation-error">{error}</div> : null}
                </div>
            );
        }

        return (
            <div ref={inputWrapRef} className="form-field-standalone" style={{ marginBottom: 0 }}>
                {fieldBody}
                {error ? <div className="validation-error">{error}</div> : null}
            </div>
        );
    }

    if (!props.editMode) {
        return <span>{formatValue(props.defaultValue)}</span>;
    }
    if (useLazyEditor && !editorActive) {
        return (
            <div
                className="form-field-standalone form-field-lazy"
                role="button"
                tabIndex={0}
                style={{
                    marginBottom: 0,
                    minHeight: 24,
                    padding: '1px 7px',
                    border: '1px solid transparent',
                    borderRadius: 4,
                    cursor: 'text',
                    width: '100%',
                }}
                onClick={() => setEditorActive(true)}
                onFocus={() => setEditorActive(true)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setEditorActive(true);
                    }
                }}
            >
                <span>{value === '' || value == null ? '\u00A0' : value}</span>
                {error ? <div className="validation-error">{error}</div> : null}
            </div>
        );
    }

    return (
        <Form.Item
            name={props.name}
            validateStatus={error ? "error" : ""}
            help={error}
            style={{ marginBottom: 0 }}
        >
            {props.allowAny ? fieldBody : (
                <Row gutter={8} align="middle" wrap={false}>
                    <Col flex="auto">
                        {renderControl()}
                    </Col>
                </Row>
            )}
        </Form.Item>
    );
};

export const SelectAllOnFocusInput = React.forwardRef<
    HTMLInputElement,
    React.InputHTMLAttributes<HTMLInputElement>
>((props, ref) => {
    const suppressMouseUpRef = React.useRef(false);

    return (
        <input
            {...props}
            ref={ref}
            onFocus={(e) => {
                props.onFocus?.(e);
                const el = e.currentTarget;
                suppressMouseUpRef.current = true;
                requestAnimationFrame(() => {
                    if (document.activeElement === el) {
                        el.select();
                    }
                });
            }}
            onMouseUp={(e) => {
                props.onMouseUp?.(e);
                if (suppressMouseUpRef.current) {
                    e.preventDefault();
                    suppressMouseUpRef.current = false;
                }
            }}
        />
    );
});

SelectAllOnFocusInput.displayName = "SelectAllOnFocusInput";

export default React.memo(FormField);